import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Calendar,
  Clock,
  TrendingUp,
  Mic,
  Dumbbell,
  PenTool,
  ChevronRight,
  Edit3
} from 'lucide-react';
import { getBaseUrl } from '@/utils/api';
import { getToken } from '@/utils/localStorage';

const API_URL = getBaseUrl();

// Helper functions for tracking assessment and workout completion
// These are called from other components (UserAssessment, WorkoutGrid)

export const markAssessmentComplete = () => {
  // Store assessment completion in localStorage for tracking
  const completions = JSON.parse(localStorage.getItem('assessmentCompletions') || '[]');
  completions.push({
    timestamp: new Date().toISOString(),
    type: 'physical_assessment'
  });
  localStorage.setItem('assessmentCompletions', JSON.stringify(completions));
};

export const recordWorkoutCompletion = (totalSeconds, cycleCount) => {
  // Store workout completion data
  const workouts = JSON.parse(localStorage.getItem('workoutCompletions') || '[]');
  workouts.push({
    timestamp: new Date().toISOString(),
    duration_seconds: totalSeconds,
    cycles_completed: cycleCount
  });
  localStorage.setItem('workoutCompletions', JSON.stringify(workouts));
  
  // Also try to record to backend if user is authenticated
  try {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    fetch(`${API_URL}/api/sessions/history`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        session_type: 'physical',
        title: 'Calibration Session',
        description: `Completed ${cycleCount} exercise cycles`,
        duration_seconds: totalSeconds,
        completed: true
      })
    }).catch(() => {
      // Silently fail - user might not be authenticated
    });
  } catch {
    // Ignore errors
  }
};

const SESSION_ICONS = {
  physical: Dumbbell,
  voice: Mic,
  writing: PenTool
};

const SESSION_COLORS = {
  physical: { bg: 'bg-burnt-terra/10', text: 'text-burnt-terra', border: 'border-burnt-terra/20' },
  voice: { bg: 'bg-shadow-pine/10', text: 'text-shadow-pine', border: 'border-shadow-pine/20' },
  writing: { bg: 'bg-obsidian-night/10', text: 'text-obsidian-night', border: 'border-obsidian-night/20' }
};

const ProgressPage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingReflection, setEditingReflection] = useState(null);
  const [reflectionText, setReflectionText] = useState('');

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      // Fetch progress summary
      const progressRes = await fetch(`${API_URL}/api/progress/summary`, {
        credentials: 'include'
      });
      if (progressRes.ok) {
        setProgress(await progressRes.json());
      }

      // Fetch session history
      const sessionType = filter === 'all' ? '' : `?session_type=${filter}`;
      const sessionsRes = await fetch(`${API_URL}/api/sessions/history${sessionType}`, {
        credentials: 'include'
      });
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveReflection = async (sessionId) => {
    try {
      const token = getToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`${API_URL}/api/sessions/history/${sessionId}/reflection`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ reflection: reflectionText })
      });
      
      // If substantial reflection (50+ characters), also record as writing session
      if (reflectionText.trim().length >= 50) {
        const session = sessions.find(s => s.id === sessionId);
        await fetch(`${API_URL}/api/sessions/history`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            session_type: 'writing',
            title: `Post-Workout Reflection`,
            description: reflectionText.substring(0, 100) + (reflectionText.length > 100 ? '...' : ''),
            duration_seconds: Math.max(60, reflectionText.length / 2),
            completed: true,
            metadata: {
              reflection_for_session: sessionId,
              original_session_type: session?.session_type,
              original_session_title: session?.title,
              word_count: reflectionText.split(/\s+/).length
            }
          })
        });
      }
      
      setSessions(sessions.map(s => 
        s.id === sessionId ? { ...s, reflection: reflectionText } : s
      ));
      setEditingReflection(null);
      setReflectionText('');
    } catch (error) {
      console.error('Error saving reflection:', error);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-mist flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burnt-terra"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-mist">
      {/* Header */}
      <header className="border-b border-burnt-terra/20 bg-white/80 backdrop-blur-sm sticky top-0 z-10 pt-safe">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/client/hub')}
            className="text-shadow-pine hover:text-burnt-terra -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-display text-xl font-bold text-obsidian-night">Your Progress</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Summary Cards */}
        {progress && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-obsidian-night">{progress.total_sessions}</p>
              <p className="text-xs text-shadow-pine/70">Sessions</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-obsidian-night">{formatDuration(progress.total_time_seconds)}</p>
              <p className="text-xs text-shadow-pine/70">Total Time</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-obsidian-night">{progress.current_streak}</p>
              <p className="text-xs text-shadow-pine/70">Day Streak</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-obsidian-night">{progress.sessions_this_week}</p>
              <p className="text-xs text-shadow-pine/70">This Week</p>
            </div>
          </div>
        )}

        {/* Pillar Breakdown */}
        {progress && (
          <div className="bg-white rounded-xl p-4 mb-6">
            <h3 className="font-display font-bold text-obsidian-night mb-3">By Pillar</h3>
            <div className="flex gap-4">
              <div className="flex-1 flex items-center gap-2">
                <div className="w-8 h-8 bg-burnt-terra/10 rounded-lg flex items-center justify-center">
                  <Dumbbell className="w-4 h-4 text-burnt-terra" />
                </div>
                <div>
                  <p className="font-bold text-obsidian-night">{progress.physical_sessions}</p>
                  <p className="text-xs text-shadow-pine/70">Physical</p>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="w-8 h-8 bg-shadow-pine/10 rounded-lg flex items-center justify-center">
                  <Mic className="w-4 h-4 text-shadow-pine" />
                </div>
                <div>
                  <p className="font-bold text-obsidian-night">{progress.voice_sessions}</p>
                  <p className="text-xs text-shadow-pine/70">Voice</p>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="w-8 h-8 bg-obsidian-night/10 rounded-lg flex items-center justify-center">
                  <PenTool className="w-4 h-4 text-obsidian-night" />
                </div>
                <div>
                  <p className="font-bold text-obsidian-night">{progress.writing_sessions}</p>
                  <p className="text-xs text-shadow-pine/70">Writing</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {['all', 'physical', 'voice', 'writing'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-burnt-terra text-white'
                  : 'bg-white text-shadow-pine hover:bg-ivory-mist'
              }`}
            >
              {f === 'all' ? 'All Sessions' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Session History */}
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <Calendar className="w-12 h-12 text-shadow-pine/30 mx-auto mb-3" />
              <p className="text-shadow-pine/70">No sessions yet</p>
              <p className="text-sm text-shadow-pine/50 mt-1">
                Complete a practice session to see it here
              </p>
            </div>
          ) : (
            sessions.map(session => {
              const Icon = SESSION_ICONS[session.session_type] || Calendar;
              const colors = SESSION_COLORS[session.session_type] || SESSION_COLORS.physical;
              
              return (
                <div key={session.id} className={`bg-white rounded-xl p-4 border ${colors.border}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-obsidian-night">{session.title}</p>
                          <div className="flex items-center gap-3 text-xs text-shadow-pine/60 mt-1">
                            <span>{formatDate(session.created_at)}</span>
                            {session.duration_seconds && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDuration(session.duration_seconds)}
                              </span>
                            )}
                          </div>
                        </div>
                        {session.completed && (
                          <span className="text-xs bg-shadow-pine/20 text-shadow-pine px-2 py-0.5 rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      
                      {session.description && (
                        <p className="text-sm text-shadow-pine/70 mt-2">{session.description}</p>
                      )}
                      
                      {/* Reflection */}
                      {editingReflection === session.id ? (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={reflectionText}
                            onChange={(e) => setReflectionText(e.target.value)}
                            placeholder="Add your reflection..."
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none h-20"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveReflection(session.id)}
                              className="bg-burnt-terra hover:bg-burnt-terra/90 text-white"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingReflection(null);
                                setReflectionText('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : session.reflection ? (
                        <div className="mt-3 p-3 bg-ivory-mist rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-shadow-pine italic">&ldquo;{session.reflection}&rdquo;</p>
                            <button
                              onClick={() => {
                                setEditingReflection(session.id);
                                setReflectionText(session.reflection);
                              }}
                              className="text-shadow-pine/50 hover:text-shadow-pine"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingReflection(session.id);
                            setReflectionText('');
                          }}
                          className="mt-2 text-xs text-burnt-terra hover:underline flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" />
                          Add reflection
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
};

export default ProgressPage;
